"use client";

import { useCallback, useState } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { PHONE_FORMAT } from "@shared/formats/phone";
import { SUBMISSION_ORIGIN } from "@convex/constants/channels.lib";
import { api } from "@convex/_generated/api";
import { useMutationWithState } from "@/hooks/use-mutation-with-state";
import { useSubmitTimeAttribution } from "@/hooks/use-lead-attribution";
import type { ProductDetail } from "./types";
import {
  trackQuoteRequestBlocked,
  trackQuoteRequestSubmitted,
  useTrackQuoteFormStart,
  useTrackQuoteFormView,
} from "./listing-quote-tracking";
import { QuoteSubmittedCard } from "./quote-submitted-card";
import { ExistingListingRequest } from "./existing-listing-request";
import { QuoteRequestFields } from "./listing-quote-request-fields";
import { useGuestQuoteSignIn } from "./use-guest-quote-sign-in";
import { GuestCodeStep, useEmailedListingCode } from "./listing-guest-code-step";
import {
  clearPendingGuestQuote,
  readPendingGuestQuote,
  writePendingGuestQuote,
  type PendingGuestQuote,
} from "./pending-guest-quote-storage";
import {
  buildInitialQuoteFormState,
  joinContactName,
  splitContactName,
  type QuoteContactMode,
  type QuoteFormState,
  type UpdateQuoteFormState,
} from "./listing-quote-request-state";
import {
  deriveFormValidity,
  everyRequirementTouched,
  type QuoteRequirementField,
} from "./listing-quote-requirements";

interface IncompleteAccountProfile {
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
}

interface ListingQuoteRequestFormProps {
  data: ProductDetail;
  initialQuantity: number;
  initialLine1?: string;
  initialLine2?: string;
  initialCity?: string;
  initialState?: string;
  initialZip?: string;
  initialCountry?: string;
  initialDeliveryDate?: string;
  canReachDashboard?: boolean;
  contactEmail?: string;
  contactPhone?: string;
  guest?: boolean;
  restorePendingCodeStep?: boolean;
  incompleteAccountProfile?: IncompleteAccountProfile;
}

export function ListingQuoteRequestForm(props: ListingQuoteRequestFormProps) {
  const form = useListingQuoteForm(props);
  useTrackQuoteFormView(props.data);

  if (form.state.existingRequest) {
    return (
      <ExistingListingRequest
        request={form.state.existingRequest}
        canReachDashboard={props.canReachDashboard ?? false}
        email={props.contactEmail ?? ""}
      />
    );
  }
  if (form.state.submittedLeadNumber) {
    return (
      <QuoteSubmittedCard
        leadNumber={form.state.submittedLeadNumber}
        email={props.contactEmail ?? ""}
      />
    );
  }
  if (form.contactMode === "guest" && form.guestSignIn.codeSentTo) {
    return (
      <GuestCodeStep
        emailedCode={form.emailedCode}
        contact={form.state.contact}
        guestSignIn={form.guestSignIn}
        buildRequest={() => buildSubmissionArgs(props.data, form.state, form.readAttribution())}
      />
    );
  }
  return (
    <QuoteRequestFields
      data={props.data}
      contactMode={form.contactMode}
      pickupAvailable={form.pickupAvailable}
      state={form.state}
      submission={form.submission}
      updateState={form.updateState}
      markStarted={form.markStarted}
      validity={form.validity}
      contactEmail={props.contactEmail ?? ""}
      contactPhone={props.contactPhone ?? ""}
    />
  );
}

function useListingQuoteForm(props: ListingQuoteRequestFormProps) {
  const [contactMode] = useState<QuoteContactMode>(() =>
    resolveContactMode(props),
  );
  const restoresGuest = contactMode === "guest" && props.restorePendingCodeStep === true;
  const [pending] = useState(() =>
    restoresGuest ? readPendingGuestQuote(props.data._id) : null,
  );
  const emailedCode = useEmailedListingCode(restoresGuest, pending);
  const [state, updateState, markStarted] = useQuoteFormState(props, pending);
  const pickupAvailable = props.data.pickupAddress !== null;
  const validity = deriveFormValidity(state, pickupAvailable, contactMode);
  const guestSignIn = useGuestQuoteSignIn({
    restoredEmail: pending?.email ?? null,
    onCodeSent: (email) => writePendingGuestQuote(props.data._id, email, state),
    onCodeStepLeft: () => clearPendingGuestQuote(props.data._id),
    onSubmitted: (submission) => trackSubmitted(props.data, state, submission),
  });
  const readAttribution = useSubmitTimeAttribution();
  const submission = useQuoteSubmission({
    canReachDashboard: props.canReachDashboard ?? false,
    canSubmit: validity.canSubmit,
    blockedByField: validity.blockedByField,
    contactMode,
    data: props.data,
    guestSignIn,
    readAttribution,
    state,
    updateState,
  });
  return {
    emailedCode,
    state,
    updateState,
    markStarted,
    contactMode,
    pickupAvailable,
    validity,
    guestSignIn,
    readAttribution,
    submission,
  };
}

function trackSubmitted(
  data: ProductDetail,
  state: QuoteFormState,
  result: { leadId: string; leadNumber: string },
) {
  trackQuoteRequestSubmitted({
    listingId: data._id,
    productTypeSlug: data.productTypeSlug || "pallets",
    productTypeName: data.productType?.displayName || data.title,
    quantity: state.quantity,
    city: state.deliveryAddress.city,
    state: state.deliveryAddress.state,
    leadId: result.leadId,
    leadNumber: result.leadNumber,
  });
}

function resolveContactMode(
  props: ListingQuoteRequestFormProps,
): QuoteContactMode {
  if (props.guest) return "guest";
  return props.incompleteAccountProfile ? "account-profile" : "account";
}

function useQuoteFormState(
  props: ListingQuoteRequestFormProps,
  pending: PendingGuestQuote | null,
) {
  const [state, setState] = useState<QuoteFormState>(() => {
    const initial = buildInitialQuoteFormState({
      ...props,
      initialContact: {
        name: joinContactName(
          props.incompleteAccountProfile?.firstName,
          props.incompleteAccountProfile?.lastName,
        ),
        phone: PHONE_FORMAT.format(props.incompleteAccountProfile?.phone ?? ""),
      },
    });
    return pending ? { ...initial, ...pending.form } : initial;
  });
  const { markStarted, markPatchStarted } = useTrackQuoteFormStart(
    props.data._id,
  );
  const updateState = useCallback<UpdateQuoteFormState>(
    (patch) => {
      markPatchStarted(patch);
      setState((current) => ({ ...current, ...patch }));
    },
    [markPatchStarted],
  );
  return [state, updateState, markStarted] as const;
}

interface QuoteSubmissionInput {
  canReachDashboard: boolean;
  canSubmit: boolean;
  blockedByField: QuoteRequirementField | null;
  contactMode: QuoteContactMode;
  data: ProductDetail;
  guestSignIn: ReturnType<typeof useGuestQuoteSignIn>;
  readAttribution: ReturnType<typeof useSubmitTimeAttribution>;
  state: QuoteFormState;
  updateState: UpdateQuoteFormState;
}

function useMemberSubmission(input: QuoteSubmissionInput) {
  const router = useRouter();
  const mutation = useMutationWithState(
    api.quotes.mutations.listingQuote.submitListingQuoteRequest,
  );
  const profileMutation = useMutationWithState(
    api.users.mutations.updateProfile,
  );
  const submit = async () => {
    if (input.contactMode === "account-profile") {
      const { name, phone } = input.state.contact;
      const savedProfile = await profileMutation.mutate({
        ...splitContactName(name),
        phone,
      });
      if (savedProfile === undefined) return;
    }
    const result = await mutation.mutate(
      buildSubmissionArgs(input.data, input.state, input.readAttribution()),
    );
    if (!result) return;
    const request = { leadId: result.leadId, leadNumber: result.leadNumber };
    if (result.alreadyRequested) {
      input.updateState({ existingRequest: request });
      return;
    }
    trackSubmitted(input.data, input.state, result);
    if (!input.canReachDashboard) {
      input.updateState({ submittedLeadNumber: request.leadNumber });
      return;
    }
    toast.success("Request sent");
    router.push(`/dashboard/orders/${result.leadId}`);
  };
  return {
    submit,
    isLoading: mutation.isLoading || profileMutation.isLoading,
    error: profileMutation.error ?? mutation.error,
    clearError: () => {
      profileMutation.clearError();
      mutation.clearError();
    },
  };
}

function useQuoteSubmission(input: QuoteSubmissionInput) {
  const member = useMemberSubmission(input);
  const guest = input.contactMode === "guest";
  const isLoading = guest ? input.guestSignIn.isBusy : member.isLoading;
  const submit = async () => {
    if (!input.canSubmit || isLoading) {
      if (input.blockedByField) {
        trackQuoteRequestBlocked(input.data._id, input.blockedByField);
      }
      input.updateState({ touched: everyRequirementTouched() });
      return;
    }
    if (guest) {
      await input.guestSignIn.start(input.state.contact);
      return;
    }
    await member.submit();
  };
  return {
    submit,
    isLoading,
    error: guest ? input.guestSignIn.error : member.error,
    clearError: guest ? input.guestSignIn.clearError : member.clearError,
  };
}

function buildSubmissionArgs(
  data: ProductDetail,
  state: QuoteFormState,
  attribution: ReturnType<ReturnType<typeof useSubmitTimeAttribution>>,
) {
  const deliveryAddress =
    state.fulfillmentType === "delivery" ? state.deliveryAddress : undefined;
  return {
    sourceProductId: data._id,
    submissionForm: SUBMISSION_ORIGIN.LISTING_QUOTE_FORM,
    sourcePath: attribution.sourcePath,
    sourceReferrer: attribution.sourceReferrer,
    utmTracking: attribution.utm,
    firstTouch: attribution.firstTouch,
    quantity: state.quantity,
    fulfillmentType: state.fulfillmentType,
    preferredDate: state.preferredDate,
    notes: state.notes.trim() || undefined,
    deliveryAddress,
  };
}
